import type { CreateOrderRequestInput, OrderRequestRow } from '#shared/types';

type RequestLine = OrderRequestRow['lines'][number];

/** One test as written on the request, and the catalogue test it was matched to. */
export interface DraftLine {
  requested_text: string;
  test_id: number | null;
}

export type RequestFields = Omit<CreateOrderRequestInput, 'lines'>;

/** A line nobody has typed into. Module scope: `useRepeater` copies it per row. */
export const EMPTY_DRAFT_LINE: DraftLine = { requested_text: '', test_id: null };

export function initialDraftLines(record: OrderRequestRow | undefined): DraftLine[] {
  return (
    record?.lines.map((line) => ({
      requested_text: line.requested_text,
      test_id: line.test_id,
    })) ?? [{ ...EMPTY_DRAFT_LINE }]
  );
}

export function initialRequestFields(record: OrderRequestRow | undefined): RequestFields {
  return {
    patient_name: record?.patient_name ?? '',
    phone_number: record?.phone_number ?? '',
    age_year: record?.age_year ?? null,
    gender: record?.gender ?? null,
    referred_by: record?.referred_by ?? '',
    source: record?.source ?? '',
    note: record?.note ?? '',
  };
}

export interface LineSummary {
  matchedCount: number;
  unmatchedCount: number;
  /** What the matched tests cost at today's list prices. */
  total: number;
}

export function summariseLines(lines: readonly RequestLine[]): LineSummary {
  const matched = lines.filter((line) => line.test_id !== null);
  return {
    matchedCount: matched.length,
    unmatchedCount: lines.length - matched.length,
    total: matched.reduce((sum, line) => sum + (line.lab_test?.price ?? 0), 0),
  };
}

export function describeMatchProgress({ matchedCount, unmatchedCount }: LineSummary): string {
  return unmatchedCount > 0
    ? `${matchedCount} matched, ${unmatchedCount} still to match`
    : `${matchedCount} matched`;
}

/**
 * A request stops being editable the moment it is dealt with: editing one that
 * became a visit would leave it describing something other than the visit it
 * produced, which is exactly the audit trail it exists to be.
 */
export function canEditRequest(status: OrderRequestRow['status']): boolean {
  return status === 'pending';
}

/**
 * A registered request became a real visit. Deleting it would erase where that
 * visit came from, and the server refuses it too.
 */
export function canDeleteRequest(status: OrderRequestRow['status']): boolean {
  return status !== 'registered';
}

/** Only a pending request can be registered or set aside, and only a declined one reopened. */
export function hasRequestActions(status: OrderRequestRow['status']): boolean {
  return status === 'pending' || status === 'declined';
}

/**
 * Setting a request aside asks for a reason first. One union rather than a
 * flag plus a string, so a reason cannot linger once the form is closed.
 */
export type DeclineState = { kind: 'idle' } | { kind: 'declining'; reason: string };
